import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { supabase } from '../lib/supabase'
import { UserPlus, Mail, CheckCircle, XCircle, AlertCircle } from 'lucide-react'

const SimpleApprovalTest = () => {
  const [testResults, setTestResults] = useState([])
  const [loading, setLoading] = useState(false)

  const addTestResult = (type, message) => {
    setTestResults(prev => [...prev, { type, message, timestamp: new Date().toLocaleTimeString() }])
  }

  const testApprovalWorkflow = async () => {
    setLoading(true)
    setTestResults([])

    try {
      // Test 1: Check if approval notifications table exists
      addTestResult('info', 'Testing approval notification system...')
      
      const { data: notificationData, error: notificationError } = await supabase
        .from('notifications')
        .select('id')
        .limit(1)

      if (notificationError) {
        addTestResult('error', `Notifications table error: ${notificationError.message}`)
      } else {
        addTestResult('success', 'Notifications table is accessible')
      }

      // Test 2: Check if user registration approvals table exists
      const { data: approvalData, error: approvalError } = await supabase
        .from('user_registration_approvals')
        .select('id')
        .limit(1)

      if (approvalError) {
        addTestResult('error', `User registration approvals table error: ${approvalError.message}`)
      } else {
        addTestResult('success', 'User registration approvals table is accessible')
      }

      // Test 3: Check if edge function exists
      try {
        const { data: functionData, error: functionError } = await supabase.functions.invoke('send-approval-notification', {
          body: {
            testMode: true,
            userEmail: 'test@example.gov',
            userName: 'Test User',
            approvalStatus: 'approved'
          }
        })
        
        if (functionError) {
          addTestResult('warning', `Edge function error: ${functionError.message}`)
        } else {
          addTestResult('success', 'Approval notification edge function is working')
        }
      } catch (edgeError) {
        addTestResult('error', `Edge function not available: ${edgeError.message}`)
      }

      // Test 4: Check current user approval status
      const { data: { user } } = await supabase.auth.getUser()
      if (user) {
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('approval_status, email')
          .eq('id', user.id)
          .single()

        if (userError) {
          addTestResult('error', `Could not fetch user data: ${userError.message}`)
        } else {
          addTestResult('info', `Current user approval status: ${userData?.approval_status || 'not set'}`)
        }
      }

      // Test 5: Check if there are any pending users
      const { data: pendingUsers, error: pendingError } = await supabase
        .from('users')
        .select('id, email, name, approval_status')
        .eq('approval_status', 'pending')

      if (pendingError) {
        addTestResult('error', `Could not fetch pending users: ${pendingError.message}`)
      } else {
        addTestResult('info', `Found ${pendingUsers?.length || 0} pending users`)
        if (pendingUsers && pendingUsers.length > 0) {
          pendingUsers.forEach(user => {
            addTestResult('info', `Pending user: ${user.email} (${user.name})`)
          })
        }
      }

      addTestResult('success', 'Approval workflow test completed!')

    } catch (error) {
      addTestResult('error', `Test failed: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  const getResultIcon = (type) => {
    switch (type) {
      case 'success': return <CheckCircle className="h-5 w-5 text-green-500" />
      case 'error': return <XCircle className="h-5 w-5 text-red-500" />
      case 'warning': return <AlertCircle className="h-5 w-5 text-yellow-500" />
      default: return <Mail className="h-5 w-5 text-blue-500" />
    }
  }

  return (
    <div className="min-h-screen bg-gov-50 p-6">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gov-800 mb-2">Simple Approval Workflow Test</h1>
          <p className="text-gov-600">Test the approval notification system functionality</p>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gov-800">Test Controls</h2>
            <button
              onClick={testApprovalWorkflow}
              disabled={loading}
              className="flex items-center px-6 py-3 bg-cyber-600 text-white rounded-lg hover:bg-cyber-700 disabled:opacity-50"
            >
              <UserPlus className="h-5 w-5 mr-2" />
              {loading ? 'Testing...' : 'Test Approval System'}
            </button>
          </div>
        </div>

        {testResults.length > 0 && (
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-semibold text-gov-800 mb-4">Test Results</h2>
            
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {testResults.map((result, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  className="flex items-start p-3 rounded-lg bg-gov-50 border border-gov-200"
                >
                  <div className="mr-3 mt-0.5">
                    {getResultIcon(result.type)}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gov-800">{result.message}</p>
                    <p className="text-xs text-gov-500 mt-1">{result.timestamp}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mt-6">
          <h3 className="font-semibold text-blue-800 mb-3">What this test checks:</h3>
          <ul className="text-sm text-blue-700 space-y-2">
            <li>• Notifications table accessibility</li>
            <li>• User registration approvals table accessibility</li>
            <li>• Edge function availability for approval notifications</li>
            <li>• Current user approval status</li>
            <li>• Pending users count and details</li>
          </ul>
        </div>
      </div>
    </div>
  )
}

export default SimpleApprovalTest